// Formatierungen für die Oberfläche. Alle Texte sind deutsch, Zahlen und
// Zeiten folgen österreichischer Schreibweise.

const dateTime = new Intl.DateTimeFormat('de-AT', {
  dateStyle: 'short',
  timeStyle: 'short',
})

const timeOnly = new Intl.DateTimeFormat('de-AT', { timeStyle: 'short' })

const dateOnly = new Intl.DateTimeFormat('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric' })

/** Nur der Tag, ohne Uhrzeit – für Zeiträume in Berichten. */
export function formatDate(iso: string | undefined | null): string {
  if (!iso) return '–'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '–' : dateOnly.format(d)
}

export function formatDateTime(iso: string | undefined | null): string {
  if (!iso) return '–'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '–' : dateTime.format(d)
}

export function formatTime(iso: string | undefined | null): string {
  if (!iso) return '–'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '–' : timeOnly.format(d)
}

/** "vor 3 min", "vor 2 h" – für Zeitpunkte in der Vergangenheit. */
export function formatAgo(iso: string | undefined | null, now: Date = new Date()): string {
  if (!iso) return 'nie'
  const then = new Date(iso)
  if (Number.isNaN(then.getTime())) return 'nie'
  const seconds = Math.round((now.getTime() - then.getTime()) / 1000)
  if (seconds < 0) return 'gerade eben'
  if (seconds < 60) return `vor ${seconds} s`
  const minutes = Math.round(seconds / 60)
  if (minutes < 60) return `vor ${minutes} min`
  const hours = Math.round(minutes / 60)
  if (hours < 48) return `vor ${hours} h`
  return `vor ${Math.round(hours / 24)} Tagen`
}

/** Zahl mit einer sinnvollen Anzahl Nachkommastellen. */
export function formatNumber(value: number, unit?: string): string {
  const abs = Math.abs(value)
  const digits = abs >= 100 ? 0 : abs >= 10 ? 1 : 2
  const text = new Intl.NumberFormat('de-AT', {
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  }).format(value)
  return unit ? `${text} ${unit}` : text
}

/** Byte-Zähler lesbar machen. */
export function formatBytes(value: number): string {
  const units = ['B', 'kB', 'MB', 'GB', 'TB', 'PB']
  let v = value
  let i = 0
  while (Math.abs(v) >= 1000 && i < units.length - 1) {
    v /= 1000
    i += 1
  }
  return `${formatNumber(v)} ${units[i]}`
}

/** Datenrate aus Bit/s: Netzwerkleute lesen Mbit/s, nicht Bytes. */
export function formatBitsPerSecond(value: number): string {
  const units = ['bit/s', 'kbit/s', 'Mbit/s', 'Gbit/s', 'Tbit/s']
  let v = value
  let i = 0
  while (Math.abs(v) >= 1000 && i < units.length - 1) {
    v /= 1000
    i += 1
  }
  return `${formatNumber(v)} ${units[i]}`
}

/**
 * Zähler mit richtigem Numerus: "1 Standort", "2 Standorte". Ohne das steht
 * auf der Übersicht "1 Standorte".
 */
export function plural(count: number, singular: string, pluralForm: string): string {
  return `${count} ${count === 1 ? singular : pluralForm}`
}

/** Uptime aus SNMP-Hundertstelsekunden. */
export function formatUptime(centiseconds: number): string {
  const totalSeconds = Math.floor(centiseconds / 100)
  const days = Math.floor(totalSeconds / 86400)
  const hours = Math.floor((totalSeconds % 86400) / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  if (days > 0) return `${days} d ${hours} h`
  if (hours > 0) return `${hours} h ${minutes} min`
  return `${minutes} min`
}

// Was die Templates über ihre eigenen Kennzahlen sagen (Name und Einheit).
// Die festen Fälle unten gehen vor; alles andere – USV, NAS, Drucker – kennt
// nur das Template.
const vorlagenInfo = new Map<string, { label?: string; unit?: string }>()

/** Name und Einheit der Kennzahlen aus den Templates übernehmen. */
export function registerMetricInfo(list: { key: string; label?: string; unit?: string }[]): void {
  for (const info of list) {
    if (!info.label && !info.unit) continue
    const known = vorlagenInfo.get(info.key)
    vorlagenInfo.set(info.key, { label: info.label ?? known?.label, unit: info.unit ?? known?.unit })
  }
}

/** Einheit eines Templates, wie sie hinter der Zahl steht. */
const EINHEITEN: Record<string, string> = {
  percent: '%',
  celsius: '°C',
  minutes: 'min',
  seconds: 's',
  volt: 'V',
  ampere: 'A',
  watt: 'W',
  rpm: 'U/min',
  ms: 'ms',
  hours: 'h',
  days: 'Tage',
  mbps: 'Mbit/s',
}

/** Wert mit der Einheit aus dem Template. */
export function formatWithUnit(value: number, unit?: string): string {
  switch (unit) {
    case 'bytes':
      return formatBytes(value)
    case 'bits_per_second':
      return formatBitsPerSecond(value)
    case 'centiseconds':
      return formatUptime(value)
    default:
      return formatNumber(value, unit ? EINHEITEN[unit] : undefined)
  }
}

/** Einheit und Aufbereitung je Metrik. */
export function describeMetric(metric: string): {
  label: string
  unit?: string
  kind: 'percent' | 'bytes' | 'ms' | 'uptime' | 'count' | 'rate'
} {
  switch (metric) {
    case 'cpu_percent':
      return { label: 'CPU', unit: '%', kind: 'percent' }
    case 'memory_percent':
      return { label: 'Arbeitsspeicher', unit: '%', kind: 'percent' }
    case 'swap_percent':
      return { label: 'Auslagerung', unit: '%', kind: 'percent' }
    case 'disk_percent':
      return { label: 'Datenträger', unit: '%', kind: 'percent' }
    case 'filesystem_percent':
      return { label: 'Dateisystem', unit: '%', kind: 'percent' }
    case 'session_count':
      return { label: 'Sitzungen', kind: 'count' }
    case 'icmp_rtt_ms':
      return { label: 'Latenz', unit: 'ms', kind: 'ms' }
    case 'icmp_jitter_ms':
      return { label: 'Jitter', unit: 'ms', kind: 'ms' }
    case 'icmp_loss_percent':
      return { label: 'Paketverlust', unit: '%', kind: 'percent' }
    case 'sys_uptime_ticks':
      return { label: 'Laufzeit', kind: 'uptime' }
    case 'if_in_octets':
      return { label: 'Eingehend gesamt', unit: 'B', kind: 'bytes' }
    case 'if_out_octets':
      return { label: 'Ausgehend gesamt', unit: 'B', kind: 'bytes' }
    case 'if_in_bits_per_s':
      return { label: 'Eingehend', unit: 'bit/s', kind: 'rate' }
    case 'if_out_bits_per_s':
      return { label: 'Ausgehend', unit: 'bit/s', kind: 'rate' }
    case 'if_oper_status':
      return { label: 'Port-Status', kind: 'count' }
    case 'if_speed_mbps':
      return { label: 'Geschwindigkeit', unit: 'Mbit/s', kind: 'count' }
    case 'flow_bits_per_s':
      return { label: 'Datenverkehr', unit: 'bit/s', kind: 'rate' }
    case 'if_in_errors':
      return { label: 'Fehler eingehend', kind: 'count' }
    case 'if_out_errors':
      return { label: 'Fehler ausgehend', kind: 'count' }
    case 'temperature_celsius':
      return { label: 'Temperatur', unit: '°C', kind: 'count' }
    case 'vpn_tunnel_status':
      return { label: 'VPN-Tunnel', kind: 'count' }
    default: {
      const info = vorlagenInfo.get(metric)
      if (info?.unit === 'percent') return { label: info.label ?? metric, unit: '%', kind: 'percent' }
      return {
        label: info?.label ?? metric,
        unit: info?.unit ? EINHEITEN[info.unit] : undefined,
        kind: 'count',
      }
    }
  }
}

/** Wert je nach Metrik formatieren. */
export function formatMetricValue(metric: string, value: number): string {
  const { unit, kind } = describeMetric(metric)
  switch (kind) {
    case 'bytes':
      return formatBytes(value)
    case 'rate':
      return formatBitsPerSecond(value)
    case 'uptime':
      return formatUptime(value)
    default:
      return formatNumber(value, unit)
  }
}
